import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';

const ManageUsers = () => {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');

    // Pagination
    const [pagination, setPagination] = useState({});
    const [page, setPage] = useState(1);
    const [debouncedSearch, setDebouncedSearch] = useState('');

    const [showEditModal, setShowEditModal] = useState(false);
    const [editingUser, setEditingUser] = useState(null);

    const [formData, setFormData] = useState({
        first_name: '',
        last_name: '',
        email: '',
        mobile: '',
        gender: '',
        description: '',
        status: 1
    });


    const handleEdit = (user) => {
        setEditingUser(user);
        setFormData({
            first_name: user.first_name || '',
            last_name: user.last_name || '',
            email: user.email || '',
            mobile: user.mobile || '',
            gender: user.gender || '',
            description: user.description || '',
            status: user.status
        });
        setShowEditModal(true);
    };

    const handleSubmitEdit = async (e) => {
        e.preventDefault();

        try {
            await api.put(`/admin/users/${editingUser.id}`, formData);
            toast.success("User updated successfully!");

            setShowEditModal(false);
            setEditingUser(null);
            fetchUsers(page);
        } catch (error) {
            toast.error(error.response?.data?.message || "Failed to update user");
        }
    };




    /** Debounce search */
    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearch(searchTerm);
        }, 800);

        return () => clearTimeout(handler);
    }, [searchTerm]);

    /** Refetch whenever filters change */
    useEffect(() => {
        fetchUsers(1);
    }, [debouncedSearch, statusFilter]);

    const fetchUsers = async (pageNumber = 1) => {
        try {
            setLoading(true);

            const params = {
                page: pageNumber,
                search: debouncedSearch || undefined,
                status: statusFilter !== 'all' ? statusFilter : undefined,
            };

            const response = await api.get('/admin/users', { params });

            const paginator = response.data.data;

            setUsers(paginator.data || []);
            setPagination(paginator);
            setPage(pageNumber);
        } catch (error) {
            toast.error('Failed to load users');
            console.error('Error fetching users:', error);
        } finally {
            setLoading(false);
        }
    };

    /** Toggle Status */
    const handleStatusToggle = async (userId, currentStatus) => {
        const action = currentStatus === 1 ? 'Deactivate' : 'Activate';

        const result = await Swal.fire({
            title: `${action} User`,
            text: `Are you sure you want to ${action.toLowerCase()} this user?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: action,
            cancelButtonText: 'Cancel',
        });

        if (!result.isConfirmed) return;

        try {
            const newStatus = currentStatus === 1 ? 0 : 1;
            await api.put(`/admin/users/${userId}/status`, { status: newStatus });

            if (newStatus === 1) {
                toast.success('User activated successfully');
            } else {
                toast.success('User deactivated successfully');
            }

            fetchUsers(page);
        } catch (error) {
            toast.error('Failed to update user status');
        }
    };

    /** Delete */
    const handleDelete = async (userId) => {
        const result = await Swal.fire({
            title: 'Delete User',
            text: 'Are you sure you want to delete this user? This will also delete their orders and tickets.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Delete',
            cancelButtonText: 'Cancel',
        });

        if (!result.isConfirmed) return;

        try {
            await api.delete(`/admin/users/${userId}`);
            toast.success('User deleted successfully');
            fetchUsers(page);
        } catch (error) {
            toast.error('Failed to delete user');
        }
    };

    /** Pagination UI */
    const renderPagination = () => {
        if (!pagination.last_page || pagination.last_page <= 1) return null;

        const current = pagination.current_page;
        const last = pagination.last_page;

        // Number of pages to show
        const maxPages = 3;

        // Calculate start & end page
        let start = Math.max(1, current - Math.floor(maxPages / 2));
        let end = start + maxPages - 1;

        if (end > last) {
            end = last;
            start = Math.max(1, end - maxPages + 1);
        }

        const pageNumbers = [];
        for (let i = start; i <= end; i++) {
            pageNumbers.push(i);
        }

        return (
            <nav className="mt-4">
                <ul className="pagination justify-content-center">

                    {/* Previous */}
                    <li className={`page-item ${current === 1 ? 'disabled' : ''}`}>
                        <button className="page-link" onClick={() => fetchUsers(current - 1)}>
                            Previous
                        </button>
                    </li>

                    {/* "1" + ellipsis */}
                    {start > 1 && (
                        <>
                            <li className="page-item">
                                <button className="page-link" onClick={() => fetchUsers(1)}>1</button>
                            </li>
                            {start > 2 && (
                                <li className="page-item disabled">
                                    <span className="page-link">...</span>
                                </li>
                            )}
                        </>
                    )}

                    {/* Dynamic page numbers */}
                    {pageNumbers.map(num => (
                        <li key={num} className={`page-item ${num === current ? 'active' : ''}`}>
                            <button className="page-link" onClick={() => fetchUsers(num)}>
                                {num}
                            </button>
                        </li>
                    ))}

                    {/* Right ellipsis + last page */}
                    {end < last && (
                        <>
                            {end < last - 1 && (
                                <li className="page-item disabled">
                                    <span className="page-link">...</span>
                                </li>
                            )}
                            <li className="page-item">
                                <button className="page-link" onClick={() => fetchUsers(last)}>
                                    {last}
                                </button>
                            </li>
                        </>
                    )}

                    {/* Next */}
                    <li className={`page-item ${current === last ? 'disabled' : ''}`}>
                        <button className="page-link" onClick={() => fetchUsers(current + 1)}>
                            Next
                        </button>
                    </li>

                </ul>
            </nav>
        );
    };


    return (
        <div className="container-fluid">
            <div className="d-sm-flex align-items-center justify-content-between mb-4">
                <h1 className="h3 mb-0 font-weight-bold text-gray-900">Manage Users</h1>
            </div>

            {/* Filters */}
            <div className="card shadow mb-4">
                <div className="card-body">
                    <div className="row">
                        <div className="col-md-6">
                            <input
                                type="text"
                                className="form-control"
                                placeholder="Search by name or email..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>
                        <div className="col-md-3">
                            <select
                                className="form-control"
                                value={statusFilter}
                                onChange={(e) => setStatusFilter(e.target.value)}
                            >
                                <option value="all">All Status</option>
                                <option value="active">Active</option>
                                <option value="inactive">Inactive</option>
                            </select>
                        </div>
                    </div>
                </div>
            </div>

            {/* Users Table */}
            <div className="card shadow mb-4">
                <div className="card-header py-3">
                    <h6 className="m-0 font-weight-bold text-gray-900">
                        All Users ({pagination.total || users.length})
                    </h6>
                </div>
                <div className="card-body">
                    {loading ? (
                        <div className="text-center py-4">
                            <div className="spinner-border text-primary" role="status">
                                <span className="sr-only">Loading...</span>
                            </div>
                        </div>
                    ) : (
                    <div className="table-responsive">
                        <table className="table table-bordered" width="100%" cellSpacing="0">
                            <thead>
                            <tr>
                                <th>ID</th>
                                <th>Name</th>
                                <th>Email</th>
                                <th>Phone</th>
                                <th>Orders</th>
                                <th>Status</th>
                                <th>Joined</th>
                                <th>Actions</th>
                            </tr>
                            </thead>
                            <tbody>
                            {users.length > 0 ? (
                                users.map(user => (
                                    <tr key={user.id}>
                                        <td>{user.id}</td>
                                        <td>{user.first_name} {user.last_name}</td>
                                        <td>{user.email}</td>
                                        <td>{user.mobile || 'N/A'}</td>
                                        <td>
                        <span className="badge badge-primary">
                          {user.orders_count || 0} orders
                        </span>
                                        </td>
                                        <td>
                        <span className={`badge ${user.status === 1 ? 'badge-success' : 'badge-danger'}`}>
                          {user.status === 1 ? 'Active' : 'Inactive'}
                        </span>
                                        </td>
                                        <td>{new Date(user.created_at).toLocaleDateString()}</td>
                                        <td>
                                            <button
                                                onClick={() => handleStatusToggle(user.id, user.status)}
                                                className={`p-2 ${user.status === 1 ? 'text-warning hover:bg-warning' : 'text-success'} rounded transition-colors border-0 bg-transparent`}
                                                style={{ background: 'transparent' }}
                                                title={user.status === 1 ? 'Deactivate' : 'Activate'}
                                                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = user.status === 1 ? '#fff3cd' : '#d1e7dd'}
                                                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                                            >
                                                {user.status === 1 ? (
                                                    <svg className="w-5 h-5" style={{ width: '1.25rem', height: '1.25rem' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                                                    </svg>
                                                ) : (
                                                    <svg className="w-5 h-5" style={{ width: '1.25rem', height: '1.25rem' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                    </svg>
                                                )}
                                            </button>
                                            <button
                                                onClick={() => handleEdit(user)}
                                                className="p-2 text-primary rounded transition-colors border-0 bg-transparent"
                                                style={{ background: 'transparent' }}
                                                title="Edit User"
                                                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#cfe2ff'}
                                                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                                            >
                                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
                                                          d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-5-9l6 6M16 3l5 5"/>
                                                </svg>
                                            </button>

                                            <button
                                                onClick={() => handleDelete(user.id)}
                                                className="p-2 text-danger rounded transition-colors border-0 bg-transparent"
                                                style={{ background: 'transparent' }}
                                                title="Delete"
                                                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8d7da'}
                                                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                                            >
                                                <svg className="w-5 h-5" style={{ width: '1.25rem', height: '1.25rem' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                </svg>
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr><td colSpan="8" className="text-center">No users found</td></tr>
                            )}
                            </tbody>
                        </table>

                        {/* Pagination */}
                        {renderPagination()}
                    </div>
                    )}
                </div>
            </div>
            {showEditModal && (
                <div
                    className="modal fade show"
                    style={{
                        display: "block",
                        overflowY: "auto",     // <-- IMPORTANT: allows whole-page scroll on mobile
                        background: "rgba(0,0,0,0.5)"
                    }}
                >
                <div className="modal-dialog modal-lg modal-dialog-scrollabl">
                        <div className="modal-content">

                            <div className="modal-header">
                                <h5 className="modal-title">Edit User</h5>
                                <button className="close" onClick={() => setShowEditModal(false)}>
                                    <span>&times;</span>
                                </button>
                            </div>

                            <form onSubmit={handleSubmitEdit}>
                                <div className="modal-body">

                                    <div className="row">
                                        <div className="col-md-6 mb-3">
                                            <label>First Name</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                value={formData.first_name}
                                                onChange={(e) => setFormData({...formData, first_name: e.target.value})}
                                            />
                                        </div>

                                        <div className="col-md-6 mb-3">
                                            <label>Last Name</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                value={formData.last_name}
                                                onChange={(e) => setFormData({...formData, last_name: e.target.value})}
                                            />
                                        </div>

                                        <div className="col-md-6 mb-3">
                                            <label>Email</label>
                                            <input
                                                type="email"
                                                className="form-control"
                                                value={formData.email}
                                                onChange={(e) => setFormData({...formData, email: e.target.value})}
                                            />
                                        </div>

                                        <div className="col-md-6 mb-3">
                                            <label>Phone</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                value={formData.mobile}
                                                onChange={(e) => setFormData({...formData, mobile: e.target.value})}
                                            />
                                        </div>

                                        <div className="col-md-6 mb-3">
                                            <label>Gender</label>
                                            <select
                                                className="form-control"
                                                value={formData.gender ?? ''}
                                                onChange={(e) => setFormData({...formData, gender: e.target.value ? Number(e.target.value) : null})}
                                            >
                                                <option value="">Select</option>
                                                <option value="1">Male</option>
                                                <option value="2">Female</option>
                                            </select>
                                        </div>

                                        <div className="col-md-6 mb-3">
                                            <label>Status</label>
                                            <select
                                                className="form-control"
                                                value={formData.status}
                                                onChange={(e) => setFormData({...formData, status: Number(e.target.value)})}
                                            >
                                                <option value={1}>Active</option>
                                                <option value={0}>Inactive</option>
                                            </select>
                                        </div>

                                        <div className="col-md-12 mb-3">
                                            <label>Description</label>
                                            <textarea
                                                className="form-control"
                                                value={formData.description}
                                                onChange={(e) => setFormData({...formData, description: e.target.value})}
                                            ></textarea>
                                        </div>
                                    </div>

                                </div>

                                <div className="modal-footer">
                                    <button type="submit" className="btn btn-sm btn-success">
                                        Save Changes
                                    </button>
                                </div>
                            </form>

                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ManageUsers;
